import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ArrowLeft, Clock, User, Send, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForumPostPage() {
    const { postId } = useParams();
    const { user } = useAuth();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [replyContent, setReplyContent] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchPost();
    }, [postId]);

    const fetchPost = async () => {
        try {
            const { data } = await axios.get(`${API_URL}/api/forum/posts/${postId}`, { withCredentials: true });
            setPost(data);
        } catch (error) {
            console.error('Error fetching post:', error);
            toast.error('Failed to load discussion');
        } finally {
            setLoading(false);
        }
    };

    const handleReply = async (e) => {
        e.preventDefault();
        if (!replyContent.trim()) {
            toast.error('Please enter a reply');
            return;
        }

        setSubmitting(true);
        try {
            await axios.post(
                `${API_URL}/api/forum/posts/${postId}/replies`,
                { content: replyContent },
                { withCredentials: true }
            );
            toast.success('Reply posted');
            setReplyContent('');
            fetchPost();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to post reply');
        } finally {
            setSubmitting(false);
        }
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getTierBadgeClass = (tier) => {
        switch (tier) {
            case 'seeker': return 'tier-badge tier-seeker';
            case 'walker': return 'tier-badge tier-walker';
            case 'architect': return 'tier-badge tier-architect';
            case 'partnership': return 'tier-badge tier-partnership';
            default: return 'tier-badge bg-gray-100 text-gray-600';
        }
    };

    const canModerate = user?.role === 'admin' || user?.membership_tier === 'partnership';

    const handleDeleteReply = async (replyId) => {
        if (!window.confirm('Delete this reply?')) return;
        try {
            await axios.delete(`${API_URL}/api/forum/posts/${postId}/replies/${replyId}`, { withCredentials: true });
            toast.success('Reply deleted');
            fetchPost();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to delete reply');
        }
    };

    const handleDeletePost = async () => {
        if (!window.confirm('Delete this entire discussion?')) return;
        try {
            await axios.delete(`${API_URL}/api/forum/posts/${postId}`, { withCredentials: true });
            toast.success('Discussion deleted');
            window.location.href = '/forum';
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to delete');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#F9F6F0]">
                <Navbar />
                <div className="pt-24 pb-16 flex items-center justify-center">
                    <div className="text-center">
                        <div className="w-12 h-12 border-4 border-[#5B2D8E] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-[#4A4A68]">Loading discussion...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!post) {
        return (
            <div className="min-h-screen bg-[#F9F6F0]">
                <Navbar />
                <div className="pt-24 pb-16 text-center">
                    <h2 className="font-serif text-2xl text-[#1A1A2E] mb-4">Discussion not found</h2>
                    <Link to="/forum" className="text-[#5B2D8E] hover:underline">
                        Return to Forum
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F9F6F0]">
            <Navbar />

            <main className="pt-24 pb-16" data-testid="forum-post-page">
                <div className="max-w-4xl mx-auto px-6 sm:px-12">
                    {/* Back Link */}
                    <Link 
                        to="/forum" 
                        className="inline-flex items-center gap-2 text-[#5B2D8E] hover:underline mb-8"
                        data-testid="back-to-forum"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to Forum
                    </Link>

                    {/* Post */}
                    <article className="bg-white border border-[#E5E0D8] rounded-sm p-8 mb-8" data-testid="post-detail">
                        <div className="flex items-center gap-2 mb-4">
                            <span className="px-2 py-1 bg-[#F2EBE5] text-[#5B2D8E] text-xs rounded-sm capitalize">
                                {post.category}
                            </span>
                        </div>
                        
                        <h1 className="font-serif text-3xl text-[#1A1A2E] mb-6" data-testid="post-title">
                            {post.title}
                        </h1>
                        
                        <div className="flex items-center gap-4 text-sm text-[#4A4A68] mb-6 pb-6 border-b border-[#E5E0D8] flex-wrap">
                            <span className="flex items-center gap-1">
                                <User className="w-4 h-4" />
                                {post.author_name}
                            </span>
                            <span className={getTierBadgeClass(post.author_tier)}>
                                {post.author_tier}
                            </span>
                            <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {formatDate(post.created_at)}
                            </span>
                            {(canModerate || post.author_id === user?.id) && (
                                <button
                                    onClick={handleDeletePost}
                                    className="ml-auto flex items-center gap-1 text-red-500 hover:bg-red-50 px-2 py-1 rounded text-xs"
                                    data-testid="delete-discussion-btn"
                                >
                                    <Trash2 className="w-4 h-4" />
                                    Delete discussion
                                </button>
                            )}
                        </div>
                        
                        <div className="prose prose-lg max-w-none">
                            <p className="font-sans text-[#1A1A2E] leading-relaxed whitespace-pre-wrap" data-testid="post-content">
                                {post.content}
                            </p>
                        </div>
                    </article>

                    {/* Replies Section */}
                    <section data-testid="replies-section">
                        <h2 className="font-serif text-2xl text-[#1A1A2E] mb-6">
                            Replies ({post.replies?.length || 0})
                        </h2>

                        {/* Reply Form */}
                        <form onSubmit={handleReply} className="mb-8" data-testid="reply-form">
                            <div className="bg-white border border-[#E5E0D8] rounded-sm p-4">
                                <textarea
                                    value={replyContent}
                                    onChange={(e) => setReplyContent(e.target.value)}
                                    placeholder="Share your thoughts..."
                                    rows={3}
                                    className="w-full resize-none border-0 p-0 focus:ring-0"
                                    data-testid="reply-input"
                                />
                                <div className="flex justify-end mt-4">
                                    <button
                                        type="submit"
                                        className="btn-primary flex items-center gap-2 text-xs px-6 py-2"
                                        disabled={submitting}
                                        data-testid="submit-reply-btn"
                                    >
                                        <Send className="w-4 h-4" />
                                        {submitting ? 'Posting...' : 'Post Reply'}
                                    </button>
                                </div>
                            </div>
                        </form>

                        {/* Replies List */}
                        {post.replies && post.replies.length > 0 ? (
                            <div className="space-y-4" data-testid="replies-list">
                                {post.replies.map((reply, index) => (
                                    <div 
                                        key={reply.id} 
                                        className="bg-white border border-[#E5E0D8] rounded-sm p-6"
                                        data-testid={`reply-${reply.id}`}
                                    >
                                        <div className="flex items-center gap-4 text-sm text-[#4A4A68] mb-4 flex-wrap">
                                            <span className="flex items-center gap-1">
                                                <User className="w-4 h-4" />
                                                {reply.author_name}
                                            </span>
                                            <span className={getTierBadgeClass(reply.author_tier)}>
                                                {reply.author_tier}
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <Clock className="w-4 h-4" />
                                                {formatDate(reply.created_at)}
                                            </span>
                                            {(canModerate || reply.author_id === user?.id) && (
                                                <button
                                                    onClick={() => handleDeleteReply(reply.id)}
                                                    className="ml-auto p-1 hover:bg-red-50 rounded"
                                                    data-testid={`delete-reply-${reply.id}`}
                                                    title="Delete reply"
                                                >
                                                    <Trash2 className="w-4 h-4 text-red-500" />
                                                </button>
                                            )}
                                        </div>
                                        <p className="font-sans text-[#1A1A2E] whitespace-pre-wrap">
                                            {reply.content}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8 bg-white border border-[#E5E0D8] rounded-sm">
                                <p className="text-[#4A4A68]">No replies yet. Be the first to respond!</p>
                            </div>
                        )}
                    </section>
                </div>
            </main>

            <Footer />
        </div>
    );
}
